import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Bell, Mail } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const SERIES_META = {
  "the-lattice": { title: "The Lattice", accent: "#c0392b", tag: "SPECULATIVE FICTION" },
  "somnium": { title: "SOMNIUM", accent: "#7d3c98", tag: "LITERARY SCI-FI" },
  "celestial": { title: "Celestial", accent: "#00a0ba", tag: "CLIMATE FICTION" },
  "the-overflow": { title: "The Overflow", accent: "#c8860a", tag: "LITERARY DRAMA" },
  "attended": { title: "ATTENDED", accent: "#2e8b57", tag: "LITERARY SCI-FI" },
};

export default function NewChaptersPage() {
  const [series, setSeries] = useState([]);
  const [email, setEmail] = useState("");
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/admin/series`).then(res => setSeries(res.data)).catch(() => {});
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const payload = { email };
      if (selectedSeries) payload.series = [selectedSeries];
      await axios.post(`${API}/api/subscribe`, payload);
      setToast({ type: "success", msg: selectedSeries ? `Subscribed to ${SERIES_META[selectedSeries]?.title}!` : "Subscribed to all series!" });
      setEmail("");
      setSelectedSeries(null);
    } catch {
      setToast({ type: "error", msg: "Something went wrong. Try again." });
    } finally {
      setSubmitting(false);
      setTimeout(() => setToast(null), 4000);
    }
  };

  return (
    <div data-testid="new-chapters-page" className="min-h-screen bg-ff-bg pt-24 md:pt-32 pb-24">
      {/* Toast */}
      {toast && (
        <div className="fixed top-20 right-6 z-50 px-5 py-3 rounded-md text-sm shadow-lg" style={{
          background: toast.type === "error" ? "#FF4343" : "#1a3a1a", color: "#FFFFFF"
        }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <header className="max-w-4xl mx-auto px-4 md:px-6 text-center mb-16">
        <p className="label text-ff-accent mb-4">LATEST DROPS</p>
        <h1 className="font-serif text-display text-ff-text mb-6">New Chapters</h1>
        <p className="text-ff-text-muted font-light text-lg max-w-2xl mx-auto">
          Fresh chapters across all active series. Subscribe to any series to get new chapters delivered straight to your inbox.
        </p>
      </header>

      {/* Series Drop Cards */}
      <section className="max-w-5xl mx-auto px-4 md:px-6 mb-20">
        <div className="grid gap-8">
          {series.map(s => {
            const meta = SERIES_META[s.slug] || {};
            const latestCh = s.loaded_chapters;
            const scheduleInfo = s.schedule
              ? `Every ${s.schedule.day_of_week} at ${s.schedule.time_utc} UTC`
              : "Schedule coming soon";

            return (
              <div
                key={s.slug}
                data-testid={`drop-card-${s.slug}`}
                className="relative bg-ff-surface border border-ff-border overflow-hidden group transition-all duration-300 hover:border-opacity-60"
                style={{ borderLeftWidth: "4px", borderLeftColor: meta.accent }}
              >
                <div className="p-6 md:p-8">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    {/* Left: series info */}
                    <div className="flex-1">
                      <p className="label mb-2" style={{ color: meta.accent }}>{meta.tag}</p>
                      <h2 className="font-serif text-2xl md:text-3xl text-ff-text mb-2">{s.title}</h2>
                      <p className="text-ff-text-muted text-sm mb-4">
                        {s.loaded_chapters} chapters available &middot; {s.subscriber_count} subscriber{s.subscriber_count !== 1 ? "s" : ""}
                      </p>

                      {/* Latest chapter link */}
                      <Link
                        to={`/${s.slug}/chapter-${latestCh}`}
                        data-testid={`read-latest-${s.slug}`}
                        className="inline-flex items-center gap-2 text-sm transition-colors group/link"
                        style={{ color: meta.accent }}
                      >
                        Read latest: Chapter {latestCh}
                        <ChevronRight size={16} className="group-hover/link:translate-x-1 transition-transform" />
                      </Link>
                    </div>

                    {/* Right: schedule + subscribe */}
                    <div className="md:text-right flex-shrink-0">
                      <div className="flex items-center gap-2 text-ff-text-muted text-xs mb-3 md:justify-end">
                        <Bell size={12} />
                        {scheduleInfo}
                      </div>
                      <button
                        data-testid={`subscribe-btn-${s.slug}`}
                        onClick={() => setSelectedSeries(selectedSeries === s.slug ? null : s.slug)}
                        className="px-5 py-2.5 text-sm font-sans tracking-wide transition-all duration-200"
                        style={{
                          background: selectedSeries === s.slug ? meta.accent : "transparent",
                          border: `1px solid ${meta.accent}`,
                          color: selectedSeries === s.slug ? "#FFFFFF" : meta.accent,
                        }}
                      >
                        {selectedSeries === s.slug ? "Selected" : "Subscribe to this series"}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Hover glow */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-[0.03] transition-opacity duration-500 pointer-events-none"
                  style={{ backgroundColor: meta.accent }}
                />
              </div>
            );
          })}
        </div>
      </section>

      {/* Subscribe Form */}
      <section className="max-w-lg mx-auto px-4 md:px-6">
        <div className="bg-ff-dark-section border border-ff-border p-8 text-center">
          <Mail size={24} className="mx-auto text-ff-accent mb-4" />
          <h3 className="font-serif text-xl text-ff-text mb-2">
            {selectedSeries ? `Subscribe to ${SERIES_META[selectedSeries]?.title}` : "Subscribe to all series"}
          </h3>
          <p className="text-ff-text-muted text-sm mb-6">
            Get new chapters delivered to your inbox. Free forever.
          </p>
          <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              data-testid="new-subscribe-email"
              className="input-dark flex-1 text-center sm:text-left"
            />
            <button
              type="submit"
              disabled={submitting}
              data-testid="new-subscribe-btn"
              className="btn-primary whitespace-nowrap disabled:opacity-50"
            >
              {submitting ? "Subscribing..." : "Subscribe"}
            </button>
          </form>
          {selectedSeries && (
            <button
              onClick={() => setSelectedSeries(null)}
              className="mt-3 text-ff-text-muted text-xs hover:text-ff-text transition-colors"
            >
              or subscribe to all series instead
            </button>
          )}
        </div>

        {/* Tier upsell */}
        <div className="mt-8 text-center">
          <p className="text-ff-text-muted text-sm mb-3">Want chapter drops delivered weekly?</p>
          <Link to="/subscribe" data-testid="upsell-link" className="inline-flex items-center gap-1 text-ff-accent text-sm hover:underline">
            View all plans <ChevronRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
